/**
 * Task-association predicates — does an `ahoy_task` association reach this
 * entity type, and (separately) is that reach user-writable?
 *
 * These are two different questions, deliberately kept apart. Conflating them
 * once shipped "Add task" on hosts the composer cannot link:
 *
 *  - RENDER a record's Tasks section — `entityTypeHasTaskAssociation` (broad).
 *    A task reaches the record through EITHER a user-writable rule OR a
 *    system-computed case-kind transitive rule. A meeting is reachable from a
 *    task only transitively via contact (`ahoy_deal_to_meeting`, source kind
 *    `case`, `value_write_access: internal`), so the section still lists a
 *    meeting's derived tasks. Backs `RecordViewContext.tasksEnabled`.
 *
 *  - CREATE a task linked to the record — `entityTypeHasWritableTaskAssociation`
 *    / `useHasWritableTaskAssociation` (strict). ONLY a user-writable rule
 *    counts. The composer writes the host link as an external
 *    `ahoy_association_added` event; a transitive rule is schema-validated to
 *    stay `value_write_access: internal`, so `useMemoCreate`'s
 *    `findCounterpartAssociation` finds no writable counterpart and drops the
 *    link on submit — the task is created unlinked from the host. Every "Add
 *    task" affordance must therefore gate on the STRICT predicate so it never
 *    opens the composer for a host whose link can't be written.
 *
 * Both mirror the backend's `matches_source` / `matches_target` kind-aware
 * matching — a plain `source_entity_type_name` check would miss case-kind
 * inherited rules (e.g. `ahoy_deal_to_contact`, which IS user-writable and so
 * legitimately enables create for a contact host).
 */

import { useMemo } from 'react';

import type { AssociationType } from '../../core/SchemaClient';
import type { AssociationMatcher } from '../../hooks/useAssociationMatchers';
import { useAssociationMatchers } from '../../hooks/useAssociationMatchers';
import { TASK_ENTITY_TYPE } from './task.constants';

/**
 * Broad predicate (RENDER gate): true when some association type links
 * `ahoy_task` (source) to `entityTypeName` (target) and is either user-writable
 * or a system-computed case-kind transitive rule. Use this only to decide
 * whether to SHOW a record's derived task relationships — never to gate a
 * create affordance (a transitive rule cannot be written, see
 * `entityTypeHasWritableTaskAssociation`).
 */
export function entityTypeHasTaskAssociation(
  associationTypes: AssociationType[],
  matchesSource: AssociationMatcher,
  matchesTarget: AssociationMatcher,
  entityTypeName: string
): boolean {
  return associationTypes.some(
    at =>
      (at.value_write_access !== 'internal' ||
        (at.transitive_via !== null && at.source_entity_kind_name != null)) &&
      matchesSource(at, TASK_ENTITY_TYPE) &&
      matchesTarget(at, entityTypeName)
  );
}

/**
 * Strict predicate (CREATE gate): true only when some USER-WRITABLE association
 * type links `ahoy_task` (source) to `entityTypeName` (target). This is the
 * predicate every "create task linked to this record" affordance must gate on —
 * an internal transitive rule is excluded because the composer's host link
 * would be dropped (or rejected) on submit.
 */
export function entityTypeHasWritableTaskAssociation(
  associationTypes: AssociationType[],
  matchesSource: AssociationMatcher,
  matchesTarget: AssociationMatcher,
  entityTypeName: string
): boolean {
  return associationTypes.some(
    at =>
      at.value_write_access !== 'internal' &&
      matchesSource(at, TASK_ENTITY_TYPE) &&
      matchesTarget(at, entityTypeName)
  );
}

/**
 * Hook form of the STRICT create gate for call sites that only have `orgId` +
 * the host entity type (they are not inside `RecordViewProvider`, so they
 * cannot read `tasksEnabled`). While association types are still loading the
 * list is empty and this returns `false`, so a task affordance stays hidden
 * until the schema confirms a writable rule exists — never showing a CTA that
 * would fail on submit.
 */
export function useHasWritableTaskAssociation(
  orgId: number | undefined,
  entityTypeName: string
): boolean {
  const { associationTypes, matchesSource, matchesTarget } =
    useAssociationMatchers(orgId);
  return useMemo(
    () =>
      entityTypeHasWritableTaskAssociation(
        associationTypes,
        matchesSource,
        matchesTarget,
        entityTypeName
      ),
    [associationTypes, matchesSource, matchesTarget, entityTypeName]
  );
}
